// src/realtime/realtimeClient.js
import * as signalR from '@microsoft/signalr';
import { refreshSession } from '../auth/sessionRefresh';
import { API_BASE } from '../services/apiConfig';

/** Hub on the same origin as the API (supports split SPA/API hosts). */
function getHubUrl() {
    const base = String(API_BASE || '');
    if (base.startsWith('/')) {
        return '/hubs/changes';
    }
    const root = base.replace(/\/api\/?$/i, '');
    return `${root}/hubs/changes`;
}

const HUB_URL = getHubUrl();

let connection;
let startPromise = null;

const joinedGroups = new Set();
const changeHandlers = new Set();

export function getConnection() {
    if (!connection) {
        connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                withCredentials: true, // send HttpOnly cookies to API host
            })
            .withAutomaticReconnect()
            .build();

        connection.on('changed', (evt) => {
            const entityName = evt?.entityName ?? evt?.EntityName;
            const entityId = evt?.entityId ?? evt?.EntityId;
            const action = evt?.action ?? evt?.Action;
            const payload = { entityName, entityId, action, raw: evt };
            changeHandlers.forEach((fn) => {
                try {
                    fn(payload);
                } catch {
                    // handler errors must not break other subscribers
                }
            });
        });

        connection.onreconnected(async () => {
            // re-join all groups we were in before the drop
            try {
                if (joinedGroups.size) {
                    await connection.invoke('JoinGroups', Array.from(joinedGroups));
                }
            } catch {
                // rejoin is best-effort after reconnect
            }
        });
    }
    return connection;
}

async function startWithRefreshFallback(conn) {
    try {
        await conn.start();
    } catch (e) {
        // Access token cookie may be expired -> refresh -> retry start once.
        // Only treat refresh failure as a dead session (not a hub URL / network blip).
        try {
            await refreshSession();
        } catch (refreshErr) {
            window.location.assign('/login?reason=expired');
            throw refreshErr;
        }
        await conn.start();
    }
}

export async function ensureConnected() {
    const conn = getConnection();

    if (conn.state === signalR.HubConnectionState.Connected) return conn;

    // de-dupe concurrent start calls
    if (!startPromise) {
        startPromise = (async () => {
            if (conn.state === signalR.HubConnectionState.Disconnected) {
                await startWithRefreshFallback(conn);
            }
        })().finally(() => {
            startPromise = null;
        });
    }

    await startPromise;
    return conn;
}

export async function joinGroups(groups) {
    const conn = await ensureConnected();
    await conn.invoke('JoinGroups', groups);
    groups.forEach((g) => joinedGroups.add(g));
}

export async function leaveGroups(groups) {
    const conn = await ensureConnected();
    await conn.invoke('LeaveGroups', groups);
    groups.forEach((g) => joinedGroups.delete(g));
}

export function onChange(handler) {
    changeHandlers.add(handler);
    return () => changeHandlers.delete(handler);
}

let settingsHandlers = new Set();

export function onTenantSettingsChanged(handler) {
    const conn = getConnection();
    if (!settingsHandlers.size) {
        conn.on('tenantSettingsChanged', (payload) => {
            settingsHandlers.forEach((fn) => { try { fn(payload); } catch { } });
        });
    }
    settingsHandlers.add(handler);
    return () => settingsHandlers.delete(handler);
}